import { FC } from "react";
import DefaultContactForm, { ContactFormData } from "./DefaultContactForm";
import {
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";
import ContactsAPI from "@/api/contacts";
import { useSnackbar, VariantType } from "notistack";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";
import {
  processCustomAttributesForCreation,
  extractCustomFieldsFromFormData,
} from "./utils/customAttributeUtils";

interface CreateContactFormProps {
  width?: number | undefined;
  onSubmit: () => void; // Refetch query from parent component
  onCancel: () => void;
  caseId?: number;
  importButton?: React.ReactNode;
}

const CreateContactForm: FC<CreateContactFormProps> = ({
  width,
  onSubmit,
  onCancel,
  caseId,
  importButton,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();

  const handleSubmit = async (data: ContactFormData) => {
    try {
      // Process custom fields for API submission
      const customAttributes = processCustomAttributesForCreation(data);
      const cleanedFormData = extractCustomFieldsFromFormData(data);

      // Prepare payload with cleaned data and custom attributes
      const payload = {
        ...cleanedFormData,
        case_id: caseId,
        ...(customAttributes.length > 0 && {
          custom_attributes: customAttributes,
        }),
      };

      await ContactsAPI.createContact(payload);
      onSubmit?.();
      enqueueSnackbar(`Contact ${data?.name || ""} created`, {
        variant: "success" as VariantType,
      });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Created Contact: ${data?.name}`,
        event: "contact:create",
        object_type: "contact",
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while creating this item. Please try again.",
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <FormTitle style={{ display: "flex", alignItems: "center" }}>
        Create Contact{" "}
        {importButton && (
          <div style={{ display: "inline-block", marginLeft: "auto" }}>
            {importButton}
          </div>
        )}
      </FormTitle>
      <DefaultContactForm
        onSubmit={handleSubmit}
        onCancel={onCancel}
        buttonProps={{ submitText: "Create Contact" }}
      />
    </FormWrapper>
  );
};

export default CreateContactForm;
